/**
 * Splash: landing page route (restructured in V4.1.T19).
 *
 * Layout, top to bottom:
 *   (1b) wordmark "drawlol" + lightning tagline.
 *   (1c) TWO CTAs:
 *          - "Create a game"  (primary sticker CTA) → POST /api/room
 *          - "Join a game"    (4-char code input INLINE to its left + INLINE
 *                              validation) → navigate to /r/CODE only on a
 *                              valid code; a missing room falls through to
 *                              RoomShell's room-gone screen.
 *        (Free Draw moved to the /about page in item 1.)
 *   (1d) a "How to play" card with three icon + one-line steps.
 *   (1e) a copyright footer + the /about discovery link (moved from the lobby
 *        in T16).
 *
 * The room-code charset/validator is owned by @drawlol/shared (T5), Splash
 * imports it so the landing can't drift from the server's lookup rules.
 *
 * NOTE: the static pre-hydration scaffold in client/index.html mirrors THIS
 * hierarchy (firstPaint.test.ts pins it). Any structural change here must be
 * mirrored there in lockstep or hydration flickers.
 */

import { useLocation } from "preact-iso";
import { useState } from "preact/hooks";
import { isValidRoomId, normalizeRoomCode, ROOM_CODE_LENGTH } from "@drawlol/shared";
import type { ConnState } from "../ws.js";
import { ScribbleButton } from "../components/ScribbleButton.js";
import { useRoomCreator } from "../hooks/useRoomCreator.js";
import { PencilInk, SpeechInk, TrophyInk } from "../marks/v1/HowToIcons.js";

type SplashProps = {
  connState: ConnState;
};

/** How-to-play steps: three one-liners, each with a tiny INK icon (audit
 * 2026-06-11: platform emoji broke the hand-drawn language). */
const HOW_TO_PLAY_STEPS: { icon: typeof PencilInk; text: string }[] = [
  { icon: PencilInk, text: "One player draws a secret word." },
  { icon: SpeechInk, text: "Everyone else races to guess it." },
  { icon: TrophyInk, text: "Faster guesses score more — most points wins." },
];

/**
 * The branded landing hero: V4.x.T20 (revised in T20-fix).
 *
 * A hand-drawn draw-and-guess vignette: a STATIC framed canvas (drawn fully
 * from frame 0: only the cat animates) with an ink-line cat (the archetypal
 * skribbl word) being drawn, plus a TWO-STEP guess feed that mirrors the real
 * game: "Alice guessed dog" with a RED X (an early wrong guess), then "Bob
 * guessed cat" with a GREEN check (the correct guess). This static end-state is
 * the deliverable; it is ALSO the reduced-motion + mobile fallback (gating is
 * CSS-only: see the .splash-hero* rules in index.html).
 *
 * On desktop + motion-allowed the cat's ink strokes draw themselves SLOWLY (a
 * deliberate, staggered hand-drawn pace via stroke-dashoffset) while the two
 * guess lines fade + small-rise in on a stagger: the wrong guess arrives early
 * (cat only partly drawn), the correct guess after the cat is recognizable. The
 * STATIC frame never animates. `replayKey` is the render key bumped by the
 * replay button: changing the element's key tears the subtree down and remounts
 * it, restarting the CSS animations from frame 0.
 *
 * The SVG markup is mirrored BYTE-FOR-BYTE in the static #app scaffold in
 * client/index.html (firstPaint.test.ts pins it) so first paint matches the
 * Preact render and hydration never flickers. Any edit here must be mirrored
 * there in lockstep.
 */
function SplashHeroArt({ replayKey }: { replayKey: number }) {
  return (
    <svg
      key={replayKey}
      class="splash-hero-art"
      viewBox="0 0 400 200"
      role="img"
      aria-hidden="true"
      data-replay-key={replayKey}
    >
      {/* Canvas / easel frame: STATIC chrome, fully drawn from frame 0. It is
          NOT a .splash-hero-stroke, so the draw-in animation never touches it. */}
      <rect
        class="splash-hero-frame"
        x="28"
        y="24"
        width="170"
        height="150"
        rx="6"
        fill="var(--paper)"
      />
      {/* The drawing: a hand-drawn cat in --ink line strokes (these self-draw). */}
      <path
        class="splash-hero-stroke"
        d="M 70 132 C 64 104 70 84 90 78 C 86 66 86 58 92 52 C 100 60 104 64 110 66 C 120 64 130 64 140 66 C 146 64 150 60 158 52 C 164 58 164 66 160 78 C 180 84 186 104 180 132 C 160 140 90 140 70 132 Z"
        fill="none"
      />
      {/* Eyes + nose: short strokes, so they stay quick to draw. */}
      <path class="splash-hero-stroke" d="M 104 98 q 4 -6 8 0" fill="none" />
      <path class="splash-hero-stroke" d="M 138 98 q 4 -6 8 0" fill="none" />
      <path class="splash-hero-stroke" d="M 122 110 l 6 6 l 6 -6" fill="none" />
      {/* Whiskers. */}
      <path class="splash-hero-stroke" d="M 96 116 l -22 -4 M 96 122 l -22 6" fill="none" />
      <path class="splash-hero-stroke" d="M 154 116 l 22 -4 M 154 122 l 22 6" fill="none" />

      {/* Two-step guess feed, items 1+2: the RIGHT column of a horizontal
          layout, beside the cat box (the frame spans x 28..198), vertically
          straddling the frame centre so the whole block reads balanced/centered.
          The full sentences start at x=262 so they fit inside the 400-wide
          viewBox with ~138 units of room (measured content right edge ~368).
          Each line FADES + small-RISES in (no scale overshoot). The WRONG guess
          (Alice, red X) arrives first; the CORRECT guess (Bob, green ✓) arrives
          after the cat is recognizable. */}
      <g class="splash-hero-guess splash-hero-guess--wrong">
        <path
          class="splash-hero-x"
          d="M 236 68 l 12 12 M 248 68 l -12 12"
          fill="none"
          stroke="var(--urgent)"
          stroke-width="3.5"
          stroke-linecap="round"
        />
        <text x="262" y="80" class="splash-hero-guess-text">
          Alice guessed dog
        </text>
      </g>
      <g class="splash-hero-guess splash-hero-guess--right">
        <path
          class="splash-hero-check"
          d="M 236 114 l 6 8 l 12 -16"
          fill="none"
          stroke="var(--correct)"
          stroke-width="3.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <text x="262" y="124" class="splash-hero-guess-text">
          Bob guessed cat
        </text>
      </g>
    </svg>
  );
}

export function Splash({ connState }: SplashProps) {
  const { route } = useLocation();
  // Create-a-game uses the shared creator hook (the same POST + navigate path
  // the /about Free Draw CTA reuses).
  const { create, creating, error, ready } = useRoomCreator(connState);
  const [joinCode, setJoinCode] = useState("");
  const [joinHint, setJoinHint] = useState<string | null>(null);
  // Bumped by the hero replay button, remounts the vignette to restart the
  // self-drawing CSS animation from frame 0 (desktop + motion-allowed only).
  const [heroReplayKey, setHeroReplayKey] = useState(0);

  /** Live-uppercase the typed code (drop anything past the code length). */
  function handleJoinInput(raw: string) {
    setJoinCode(normalizeRoomCode(raw).slice(0, ROOM_CODE_LENGTH));
    if (joinHint) setJoinHint(null);
  }

  /**
   * Validate + navigate. A valid 4-char code routes to /r/CODE (a missing room
   * then falls through to RoomShell's room-gone screen). Anything else shows a
   * friendly inline hint and does NOT navigate.
   */
  function handleJoin() {
    const code = normalizeRoomCode(joinCode);
    if (isValidRoomId(code)) {
      setJoinHint(null);
      route(`/r/${code}`);
      return;
    }
    setJoinHint(
      code.length < ROOM_CODE_LENGTH
        ? `Room codes are ${ROOM_CODE_LENGTH} characters.`
        : "That doesn't look like a room code — check the letters and numbers.",
    );
  }

  return (
    <div class="splash" id="splash-preact">
      <h1 class="wordmark">drawlol</h1>
      <p class="splash-tagline">draw and guess words, with friends</p>

      <div class="splash-hero">
        <SplashHeroArt replayKey={heroReplayKey} />
        <button
          type="button"
          class="splash-hero-replay"
          aria-label="Replay the intro animation"
          onClick={() => setHeroReplayKey((k) => k + 1)}
        >
          ↻
        </button>
      </div>

      {error && (
        <p class="status" data-state="error" role="alert">
          {error}
        </p>
      )}

      <div class="cta-row">
        <ScribbleButton
          variant="primary"
          onClick={() => create("/api/room")}
          disabled={!ready || creating}
          aria-label="Create a game room"
        >
          {creating ? "Creating…" : "Create a game"}
        </ScribbleButton>

        <div class="join-by-code">
          <input
            class="join-by-code-input"
            type="text"
            inputMode="text"
            autoComplete="off"
            autoCapitalize="characters"
            spellcheck={false}
            maxLength={ROOM_CODE_LENGTH}
            value={joinCode}
            placeholder="CODE"
            aria-label="Room code"
            onInput={(e) => handleJoinInput((e.target as HTMLInputElement).value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleJoin();
            }}
          />
          <ScribbleButton variant="secondary" onClick={handleJoin} aria-label="Join a game">
            Join a game
          </ScribbleButton>
        </div>

        {joinHint && (
          <p class="join-by-code-hint" role="alert">
            {joinHint}
          </p>
        )}
      </div>

      <section class="how-to-play" aria-label="How to play">
        <h2 class="how-to-play-title">How to play</h2>
        <ol class="how-to-play-steps">
          {HOW_TO_PLAY_STEPS.map((step) => (
            <li class="how-to-play-step" key={step.text}>
              <span class="how-to-play-icon" aria-hidden="true">
                <step.icon />
              </span>
              <span class="how-to-play-text">{step.text}</span>
            </li>
          ))}
        </ol>
      </section>

      <footer class="splash-footer">
        <a href="/about">What is this?</a>
        <span class="splash-footer-copyright">© drawlol</span>
      </footer>
    </div>
  );
}
